import React, { useState, useEffect, useRef, useCallback } from 'react';
import { IonIcon } from '@ionic/react';
import { cubeOutline } from 'ionicons/icons';
import BrowserWindow from 'components/atoms/BrowserWindow';
import './TestRuns.css';
import './AnalysisPipeline.css';

const STEPS = [
  {
    name: 'Setup Environment & Clone Repos',
    duration: '1m 12s',
    logs: [
      '$ git clone https://github.com/acme/webapp.git',
      'Cloning into \'webapp\'...',
      'remote: Enumerating objects: 4521, done.',
      'remote: Counting objects: 100% (4521/4521), done.',
      'remote: Compressing objects: 100% (1893/1893), done.',
      'Receiving objects: 100% (4521/4521), 12.8 MiB | 24.6 MiB/s, done.',
      'Resolving deltas: 100% (2901/2901), done.',
      '$ npm install',
      'added 1,247 packages in 38s',
      '✓ Environment ready',
    ],
  },
  {
    name: 'Analyze Ticket',
    duration: '45s',
    logs: [
      'Loading ticket ACME-1234: "Fix authentication timeout on mobile"',
      'Parsing ticket description and acceptance criteria...',
      'Identified 3 acceptance criteria',
      'Scanning codebase for related files...',
      'Found 8 relevant files across 3 modules',
      '  → src/auth/session.ts',
      '  → src/auth/middleware.ts',
      '  → src/mobile/auth-handler.ts',
      '  → src/config/timeouts.ts',
      'Root cause: Session TTL not accounting for mobile keep-alive intervals',
      '✓ Analysis complete',
    ],
  },
  {
    name: 'Generate Code Implementation',
    duration: '2m 8s',
    logs: [
      'Planning implementation strategy...',
      'Strategy: Extend session TTL for mobile clients with adaptive timeout',
      'Generating changes for src/auth/session.ts...',
      'Generating changes for src/config/timeouts.ts...',
      'Generating changes for src/mobile/auth-handler.ts...',
      'Running type check... ✓ No errors',
      'Running linter... ✓ 0 warnings',
      '✓ Implementation generated: 3 files changed, +44 -33 lines',
    ],
  },
  {
    name: 'Generate Test Cases',
    duration: '1m 34s',
    logs: [
      'Analyzing implementation changes...',
      'Identifying test scenarios from acceptance criteria...',
      'Generating test: "should extend session for mobile clients"',
      'Generating test: "should fallback to default TTL for desktop"',
      'Generating test: "should handle keep-alive refresh correctly"',
      'Generating test: "should reject expired mobile sessions"',
      '✓ Generated 4 test cases (2 critical, 1 high, 1 medium)',
    ],
  },
  {
    name: 'Finalize',
    duration: '18s',
    logs: [
      'Compiling analysis results...',
      'Generating summary and recommendations...',
      'Attaching code diff and test cases...',
      '✓ Report generated',
    ],
  },
];

const DEMO_DIFF = `--- a/src/auth/session.ts
+++ b/src/auth/session.ts
@@ -23,8 +23,14 @@
 export class SessionManager {
   private readonly store: SessionStore;
-  private readonly defaultTTL = 3600;
+  private readonly defaultTTL = 3600;
+  private readonly mobileTTL = 7200;
 
-  createSession(userId: string): Session {
-    return this.store.create(userId, this.defaultTTL);
+  createSession(userId: string, client?: ClientInfo): Session {
+    const ttl = client?.isMobile
+      ? this.mobileTTL
+      : this.defaultTTL;
+    return this.store.create(userId, ttl, {
+      keepAlive: client?.isMobile ?? false,
+    });
   }

--- a/src/config/timeouts.ts
+++ b/src/config/timeouts.ts
@@ -8,6 +8,8 @@
 export const TIMEOUT_CONFIG = {
   session: {
     default: 3600,
+    mobile: 7200,
+    keepAliveInterval: 300,
   },
   request: {
     default: 30000,`;

const DEMO_TESTS = [
  { title: 'should extend session TTL for mobile clients', priority: 'critical', status: 'pass' },
  { title: 'should reject expired mobile sessions', priority: 'critical', status: 'pass' },
  { title: 'should handle keep-alive refresh correctly', priority: 'high', status: 'pass' },
  { title: 'should fallback to default TTL for desktop', priority: 'medium', status: 'pass' },
];

const SUGGESTED_CHANGES = [
  { field: 'Status', from: 'Open', to: 'In Progress' },
  { field: 'Labels', from: 'bug', to: 'bug, mobile, auth' },
  { field: 'Estimate', from: '—', to: '3 points' },
];

const CheckIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="11" fill="#52c41a" />
    <path d="M7 12.5l3 3 7-7" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SpinnerIcon = () => (
  <div className="ap-spinner" />
);

const ChevronIcon = ({ open }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: open ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const AnalysisPipeline = () => {
  const [showContent, setShowContent] = useState(false);
  const [completedSteps, setCompletedSteps] = useState(-1);
  const [activeStep, setActiveStep] = useState(0);
  const [expandedSteps, setExpandedSteps] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [resultSections, setResultSections] = useState({ changes: false, diff: false, tests: false });
  const [diffCollapsed, setDiffCollapsed] = useState(true);
  const [testsCollapsed, setTestsCollapsed] = useState(true);
  const [changesCollapsed, setChangesCollapsed] = useState(true);
  const [expandedTests, setExpandedTests] = useState({});
  const animRef = useRef(null);

  const resetAndAnimate = useCallback(() => {
    setCompletedSteps(-1);
    setActiveStep(0);
    setExpandedSteps({});
    setShowResults(false);
    setResultSections({ changes: false, diff: false, tests: false });
    setDiffCollapsed(true);
    setTestsCollapsed(true);
    setChangesCollapsed(true);
    setExpandedTests({});

    let step = 0;
    const delays = [800, 1200, 1600, 1200, 600];

    const runStep = () => {
      if (step >= STEPS.length) {
        setTimeout(() => {
          setShowResults(true);
          setTimeout(() => setResultSections(s => ({ ...s, changes: true })), 300);
          setTimeout(() => {
            setResultSections(s => ({ ...s, diff: true }));
            setDiffCollapsed(false);
          }, 600);
          setTimeout(() => {
            setResultSections(s => ({ ...s, tests: true }));
            setTestsCollapsed(false);
          }, 900);
          setTimeout(() => setChangesCollapsed(false), 400);
        }, 400);
        return;
      }
      setActiveStep(step);
      animRef.current = setTimeout(() => {
        setCompletedSteps(step);
        step++;
        animRef.current = setTimeout(runStep, 200);
      }, delays[step] || 1000);
    };

    animRef.current = setTimeout(runStep, 500);
  }, []);

  useEffect(() => {
    const delayTimer = setTimeout(() => {
      setShowContent(true);
      resetAndAnimate();
    }, 1200);
    return () => {
      clearTimeout(delayTimer);
      if (animRef.current) clearTimeout(animRef.current);
    };
  }, [resetAndAnimate]);


  const toggleStep = (i) => {
    if (i > completedSteps) return;
    setExpandedSteps(prev => ({ ...prev, [i]: !prev[i] }));
  };

  const toggleTest = (i) => {
    setExpandedTests(prev => ({ ...prev, [i]: !prev[i] }));
  };

  return (
    <div className="ap-wrapper">
      <div className="gss-header">
        <IonIcon icon={cubeOutline} className="gss-header-icon" />
        <span className="gss-header-text">Analyse a ticket against the codebase in 4 stages</span>
      </div>
      <BrowserWindow variant="dark">
      {showContent && <div className="ap-content-fade">
      {/* Pipeline */}
      <div className="ap-pipeline">
        {STEPS.map((step, i) => {
          const isCompleted = i <= completedSteps;
          const isActive = i === activeStep && !isCompleted;
          const isExpanded = expandedSteps[i];
          return (
            <div key={i} className={`ap-step ${isCompleted ? 'ap-step-done' : ''} ${isActive ? 'ap-step-active' : ''}`}>
              <div className="ap-step-header" onClick={() => toggleStep(i)}>
                <div className="ap-step-left">
                  {isCompleted ? <ChevronIcon open={isExpanded} /> : <span className="ap-step-chevron-placeholder" />}
                  {isCompleted ? <CheckIcon /> : isActive ? <SpinnerIcon /> : <div className="ap-step-queued" />}
                  <span className="ap-step-name">{step.name}</span>
                </div>
                {isCompleted && <span className="ap-step-duration">{step.duration}</span>}
              </div>
              {isExpanded && (
                <div className="ap-step-logs">
                  {step.logs.map((line, li) => (
                    <div key={li} className="ap-log-line">
                      <span className="ap-log-num">{li + 1}</span>
                      <span className={`ap-log-text ${line.startsWith('✓') ? 'ap-log-success' : ''}`}>{line}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Results */}
      {showResults && (
        <div className="ap-results">
          {/* Suggested Changes */}
          {resultSections.changes && (
            <div className="ap-section ap-fade-in">
              <div className="ap-section-header" onClick={() => setChangesCollapsed(c => !c)}>
                <ChevronIcon open={!changesCollapsed} />
                <span className="ap-section-title">Suggested Ticket Changes</span>
                <span className="ap-section-badge">{SUGGESTED_CHANGES.length} changes</span>
              </div>
              {!changesCollapsed && (
                <div className="ap-changes-body">
                  {SUGGESTED_CHANGES.map((c, i) => (
                    <div key={i} className="ap-change-row">
                      <span className="ap-change-field">{c.field}</span>
                      <span className="ap-change-from">{c.from}</span>
                      <span className="ap-change-arrow">&rarr;</span>
                      <span className="ap-change-to">{c.to}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Code Diff */}
          {resultSections.diff && (
            <div className="ap-section ap-fade-in">
              <div className="ap-section-header" onClick={() => setDiffCollapsed(c => !c)}>
                <ChevronIcon open={!diffCollapsed} />
                <span className="ap-section-title">Code Changes</span>
                <span className="ap-section-stats"><span className="ap-stat-add">+44</span> <span className="ap-stat-del">-33</span></span>
              </div>
              {!diffCollapsed && (
                <div className="ap-diff-body">
                  <pre className="ap-diff-pre">{DEMO_DIFF.split('\n').map((line, i) => {
                    let cls = 'ap-diff-ctx';
                    if (line.startsWith('+')) cls = 'ap-diff-add';
                    else if (line.startsWith('-')) cls = 'ap-diff-del';
                    else if (line.startsWith('@@')) cls = 'ap-diff-hunk';
                    return <div key={i} className={`ap-diff-line ${cls}`}>{line}</div>;
                  })}</pre>
                </div>
              )}
            </div>
          )}

          {/* Test Cases */}
          {resultSections.tests && (
            <div className="ap-section ap-fade-in">
              <div className="ap-section-header" onClick={() => setTestsCollapsed(c => !c)}>
                <ChevronIcon open={!testsCollapsed} />
                <span className="ap-section-title">Test Cases</span>
                <span className="ap-section-badge">{DEMO_TESTS.length} tests</span>
              </div>
              {!testsCollapsed && (
                <div className="ap-tests-body">
                  {DEMO_TESTS.map((test, i) => (
                    <div key={i} className={`ap-test-item ap-priority-${test.priority}`}>
                      <div className="ap-test-header" onClick={() => toggleTest(i)}>
                        <div className="ap-test-left">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="11" fill="#52c41a" />
                            <path d="M7 12.5l3 3 7-7" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <span className="ap-test-title">{test.title}</span>
                        </div>
                        <div className="ap-test-right">
                          <span className={`ap-priority-badge ap-badge-${test.priority}`}>{test.priority}</span>
                          <ChevronIcon open={expandedTests[i]} />
                        </div>
                      </div>
                      {expandedTests[i] && (
                        <div className="ap-test-details">
                          <pre className="ap-test-pre">{getTestContent(i)}</pre>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      </div>}
      </BrowserWindow>
    </div>
  );
};

function getTestContent(index) {
  const contents = [
    `test('should extend session TTL for mobile clients', async () => {
  const session = await manager.createSession('user-1', {
    isMobile: true,
    userAgent: 'Mozilla/5.0 (iPhone; ...)',
  });
  expect(session.ttl).toBe(7200);
  expect(session.keepAlive).toBe(true);
});`,
    `test('should reject expired mobile sessions', async () => {
  const session = createExpiredSession({ isMobile: true });
  await expect(
    manager.validateSession(session.id)
  ).rejects.toThrow('Session expired');
});`,
    `test('should handle keep-alive refresh correctly', async () => {
  const session = await manager.createSession('user-1', {
    isMobile: true,
  });
  jest.advanceTimersByTime(6000 * 1000);
  await manager.keepAlive(session.id);
  const updated = await manager.getSession(session.id);
  expect(updated.expiresAt).toBeGreaterThan(Date.now());
});`,
    `test('should fallback to default TTL for desktop', async () => {
  const session = await manager.createSession('user-1', {
    isMobile: false,
  });
  expect(session.ttl).toBe(3600);
  expect(session.keepAlive).toBe(false);
});`,
  ];
  return contents[index] || '';
}

export default AnalysisPipeline;
